// src/components/AGTUps/Subscription/SubscriptionGateway.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Check } from 'lucide-react';
import subscriptionService from '../services/AGTUps-product-services/subscription-service';
import { debounce } from 'lodash';

const StyledCard = styled(Card)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    maxWidth: 400,
    margin: '0 auto',
    boxShadow: isWallpaperMode ? 'none' : theme.shadows[3],
    borderRadius: theme.shape.borderRadius * 2,
    overflow: 'hidden',
    transition: 'transform 0.3s ease-in-out',
    backgroundColor: isWallpaperMode
      ? 'rgba(8, 8, 8, 0.3)'
      : theme.palette.background.paper,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    '&:hover': {
      transform: 'translateY(-5px)',
    }
  };
});

const PricingHeader = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    background: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : 'linear-gradient(to bottom, #DC9844, #C88430, #B4701C)',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    color: theme.palette.common.white,
    padding: theme.spacing(2, 3),
    textAlign: 'center'
  };
});

const PriceTag = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .currency': {
    fontSize: '1.5rem',
    verticalAlign: 'super',
    marginRight: theme.spacing(0.5)
  },
  '& .period': {
    fontSize: '1rem',
    fontWeight: 400,
    marginLeft: theme.spacing(1),
    opacity: 0.8
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 5,
  padding: theme.spacing(1, 4),
  fontWeight: 600,
  boxShadow: theme.shadows[2],
  transition: 'all 0.3s ease',
  background: 'linear-gradient(to bottom, #DC9844, #C88430, #B4701C)',
  color: theme.palette.common.white,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
    background: 'linear-gradient(to bottom, #E8A555, #D49041, #C0822D)'
  }
}));

const FeatureList = styled(List)(({ theme }) => ({
  padding: theme.spacing(2, 0)
}));

const FeatureItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0.5, 0)
}));

const SubscriptionGateway = ({ onSubscriptionComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  
  // Implementar debounce para la verificación de suscripción
  const debouncedCheckSubscription = useRef(
    debounce(async () => {
      // Evitar verificaciones múltiples
      if (loading || subscriptionStatus !== null) return;
      
      try {
        setLoading(true);
        const response = await subscriptionService.getSubscriptionStatus();
        setSubscriptionStatus(response.data);
        
        // Si ya tiene una suscripción activa, notificar al componente padre
        if (response.data.isActive) {
          onSubscriptionComplete();
        }
      } catch (error) {
        setError('Error al verificar la suscripción');
        console.error(error);
      } finally {
        setLoading(false);
      }
    }, 300) // 300ms de debounce
  ).current;

  useEffect(() => {
    // Verificar el estado de la suscripción al cargar, usando la función debounced
    debouncedCheckSubscription();
    
    // Limpiar debounce al desmontar
    return () => {
      debouncedCheckSubscription.cancel();
    };
  }, [debouncedCheckSubscription]);

  const handleSubscribe = async () => {
    try {
      setLoading(true);
      const response = await subscriptionService.createCheckoutSession();
      
      // Redirigir a la página de checkout de Stripe
      window.location.href = response.data.url;
    } catch (error) {
      setError('Error al crear la sesión de pago');
      console.error(error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      justifyContent: 'center', 
      alignItems: 'center',
      height: '100%',
      padding: 3
    }}>
      <Paper
        elevation={0}
        sx={(theme) => {
          const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
          return {
            maxWidth: 800,
            width: '100%',
            p: 4,
            borderRadius: 4,
            backgroundColor: isWallpaperMode
              ? 'rgba(8, 8, 8, 0.3)'
              : 'background.paper',
            backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
            border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
            boxShadow: isWallpaperMode ? 'none' : undefined,
            textAlign: 'center'
          };
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          fontWeight="bold"
          sx={(theme) => ({
            color: theme.palette.actualMode === 'wallpaper'
              ? '#FFFFFF'
              : 'text.primary'
          })}
        >
          Choose Your Plan
        </Typography>

        <Typography
          variant="body1"
          paragraph
          sx={(theme) => ({
            mb: 4,
            color: theme.palette.actualMode === 'wallpaper'
              ? 'rgba(255, 255, 255, 0.85)'
              : 'text.secondary'
          })}
        >
          Unlock powerful features to grow your business
        </Typography>
        
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <StyledCard>
            <PricingHeader>
              <PriceTag>
                <span className="currency">$</span>
                30
                <span className="period">/month</span>
              </PriceTag>
              <Typography variant="body2">
                7-day free trial
              </Typography>
            </PricingHeader>
            
            <CardContent
              sx={(theme) => {
                const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
                return {
                  backgroundColor: isWallpaperMode ? 'transparent' : undefined,
                  '& .MuiListItemText-primary': {
                    color: isWallpaperMode ? '#FFFFFF' : undefined
                  }
                };
              }}
            >
              <FeatureList>
                <FeatureItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Check size={20} color="green" />
                  </ListItemIcon>
                  <ListItemText primary="Optimize pricing, sales strategies, and market fit" />
                </FeatureItem>
                <FeatureItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Check size={20} color="green" />
                  </ListItemIcon>
                  <ListItemText primary="Upload receipts and automate financial updates instantly" />
                </FeatureItem>
                <FeatureItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Check size={20} color="green" />
                  </ListItemIcon>
                  <ListItemText primary="Find and negotiate with suppliers through AI-powered search" />
                </FeatureItem>
                <FeatureItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Check size={20} color="green" />
                  </ListItemIcon>
                  <ListItemText primary="Get expert advice for complex business decisions" />
                </FeatureItem>
                <FeatureItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Check size={20} color="green" />
                  </ListItemIcon>
                  <ListItemText primary="Strategies tailored to every stage of your startup's journey" />
                </FeatureItem>
                <FeatureItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Check size={20} color="green" />
                  </ListItemIcon>
                  <ListItemText primary="Powerful shortcuts for increased productivity" />
                </FeatureItem>
              </FeatureList>
            </CardContent>
            
            <Divider />
            
            <CardActions sx={{ justifyContent: 'center', p: 2 }}>
              <StyledButton
                variant="contained"
                size="large"
                onClick={handleSubscribe}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Start Free Trial'}
              </StyledButton>
            </CardActions>
          </StyledCard>
        </Box>
        
        <Typography
          variant="body2"
          sx={(theme) => ({
            color: theme.palette.actualMode === 'wallpaper'
              ? 'rgba(255, 255, 255, 0.7)'
              : 'text.secondary'
          })}
        >
          Cancel anytime. No commitment required.
        </Typography>
      </Paper>
    </Box>
  );
};

export default SubscriptionGateway;